import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import A11y from 'chameleon/ui-stack/utilities/a11y';
import Icon from 'chameleon/ui-stack/ui/base/icon';

const CheckBox = (props) => {
  const {
    disabled,
    checked,
    onClick,
    tabIndex,
    background,
    accessibility,
    width,
    height,
    className,
    noDesc,
  } = props;

  const [isChecked, setIsChecked] = useState(checked);

  //reflect the ui with the incoming changes
  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  const backgrounds = {
    black: 'text-black',
    gray: 'text-gray-900',
  };

  const classNames = cn(
    'stroke-current outline-none',
    {
      'cursor-pointer': !disabled,
    },
    backgrounds[background] || null,
    className,
  );

  const _onClick = useCallback(() => {
    if (disabled) return;
    setIsChecked((currentIsChecked) => {
      const newIsChecked = !currentIsChecked;
      onClick(newIsChecked);
      return newIsChecked;
    });
  }, [onClick, disabled]);

  const onKeyPress = useCallback(
    (event) => {
      const { which: keyCode } = event;

      if ([13, 32].includes(keyCode)) {
        if (disabled) return;
        setIsChecked((currentIsChecked) => {
          const newIsChecked = !currentIsChecked;
          onClick(newIsChecked);
          return newIsChecked;
        });
      }
    },
    [onClick, disabled],
  );

  return (
    <A11y>
      <Icon
        id={isChecked ? 'checkbox_checked' : 'checkbox'}
        className={classNames}
        onClick={_onClick}
        onKeyPress={onKeyPress}
        width={width || null}
        height={height || null}
        role="checkbox"
        noDesc={noDesc}
        accessibility={{
          ...accessibility,
          'aria-checked': isChecked,
          tabIndex,
        }}
      />
    </A11y>
  );
};

CheckBox.propTypes = {
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  checked: PropTypes.bool,
  tabIndex: PropTypes.oneOf([0, 1, -1]),
  background: PropTypes.string,
  accessibility: PropTypes.shape({
    'aria-label': PropTypes.string,
  }),
  className: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  noDesc: PropTypes.bool,
};

CheckBox.defaultProps = {
  onClick: () => {},
  disabled: false,
  checked: false,
  tabIndex: 0,
  background: 'black',
  accessibility: {
    'aria-label': null,
  },
  className: '',
  width: 0,
  height: 0,
  noDesc: false,
};

export default CheckBox;
